import React, { useEffect, useMemo, useState } from "react";
import LayoutBuyerSeller from "../../components/layout/LayoutBuyerSeller";
import { HiSearch } from "react-icons/hi";
import apiService from "../../actions/apiService";
import { useAuth } from "../../context/authContext";
import ConfirmTransactionModal from "../../components/models/ConfirmTransactionModal";

const STATUS_PILL = {
  pending: "bg-yellow-100 text-yellow-700",
  accepted: "bg-blue-100 text-blue-700",
  paid: "bg-green-100 text-green-700",
  buyer_confirmed: "bg-purple-100 text-purple-700",
  confirmed: "bg-green-100 text-green-700",
  released: "bg-emerald-100 text-emerald-700",
  disputed: "bg-red-100 text-red-700",
  resolved: "bg-blue-100 text-blue-700",
  cancelled: "bg-gray-100 text-gray-700",
  payment_received: "bg-green-100 text-green-700",
};

// Helper function to get status display text and pill key for sellers
const getStatusDisplay = (
  status,
  adminVerificationStatus,
  paymentCancelledReason
) => {
  if (status === "cancelled") {
    if (paymentCancelledReason === "item_not_authentication") {
      return {
        text: "Cancelled - Authentication Failed",
        pillKey: "cancelled",
      };
    }
    return { text: "Cancelled", pillKey: "cancelled" };
  }
  if (status === "pending") {
    return {
      text: "Awaiting Escrow payment from the buyer",
      pillKey: "pending",
    };
  }
  if (status === "accepted" && adminVerificationStatus === "verified") {
    return {
      text: "Escrow paid",
      pillKey: "payment_received",
    };
  }
  if (status === "buyer_confirmed") {
    return {
      text: "Buyer confirmed, awaiting your confirmation",
      pillKey: "buyer_confirmed",
    };
  }
  if (status === "confirmed") {
    return {
      text: "Confirmed, Awaiting Escrow Transfer",
      pillKey: "confirmed",
    };
  }
  if (adminVerificationStatus === "released" || "Released") {
    return { text: "Sold", pillKey: "released" };
  }
  return {
    text: status.charAt(0).toUpperCase() + status.slice(1).replace("_", " "),
    pillKey: status,
  };
};

// Truncate a string after N characters, appending an ellipsis if truncated
const truncateChars = (str, n = 16) => {
  if (!str) return "";
  const s = String(str);
  return s.length > n ? s.slice(0, n) + "…" : s;
};

// Helper to normalize API transactions to the UI shape used by this page
const mapApiTransactionToRow = (t) => {
  if (!t || typeof t !== "object") return null;
  const listing = t.listing || t.item || {};
  const buyer = t.buyer || t.user || t.customer || {};
  // Prefer main status; fallback to payment/transfer/admin verification
  let status =
    t.status ||
    t.payment_status ||
    t.transfer_status ||
    t.admin_verification_status;
  status = (status ? String(status) : "pending").toLowerCase();
  // Status mapping is now direct - no transformation needed

  // Choose amount: buyer_paid_amount > item.price > amount/total_amount
  const amountNumRaw =
    t.seller_payout ?? listing.price ?? t.amount ?? t.total_amount ?? null;
  const amountStr =
    amountNumRaw == null
      ? "—"
      : typeof amountNumRaw === "number"
      ? `${amountNumRaw.toLocaleString()}€`
      : `${amountNumRaw}€`;

  const imageBase = process.env.REACT_APP_IMAGE_URL || "";
  return {
    id: t.id || t.transaction_id || t.reference || "—",
    listing: {
      title: listing.title || listing.name || "—",
      type: listing.custom_category
        ? `${listing.custom_category}`
        : listing.category || "—",
      thumb:
        (Array.isArray(listing.images) && listing.images[0]?.file_url
          ? imageBase + listing.images[0]?.file_url
          : null) ||
        listing.thumbnail_url ||
        listing.image_url ||
        "/static/img/placeholder.jpg",
    },
    buyer: {
      name:
        [buyer.firstName || buyer.first_name, buyer.lastName || buyer.last_name]
          .filter(Boolean)
          .join(" ") ||
        buyer.name ||
        buyer.email ||
        "—",
      email: buyer.email || "—",
      avatar: buyer.profile_image_url || buyer.profile_image_url,
    },
    amount: amountStr,
    status: String(status).toLowerCase(),
    sellerConfirmed: Boolean(t.seller_confirmed),
    adminVerificationStatus: t.admin_verification_status
      ? String(t.admin_verification_status).toLowerCase()
      : "",
    paymentCancelledReason: t.payment_cancelled_reason || null,
    itemId: listing.id,
  };
};

const SellerTransactions = () => {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("recent");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rowsApi, setRowsApi] = useState([]);
  const [confirmingId, setConfirmingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [modalError, setModalError] = useState(null);

  // Fetch transactions for the current user (seller/buyer list by user)
  useEffect(() => {
    let active = true;
    const run = async () => {
      if (!user?.id) return;
      setLoading(true);
      setError(null);
      try {
        const idParamKey = user?.role === "buyer" ? "buyer_id" : "seller_id";
        const params = {
          [idParamKey]: user.id,
          search: search?.trim() || null,
          status: status !== "all" ? status : null,
          page: 1,
          limit: 100,
        };
        const res = await apiService.get("/transactions/by-user/list", params);
        if (!active) return;
        if (res?.success) {
          const list = res?.data?.transactions;
          const normalized = (Array.isArray(list) ? list : [])
            .map(mapApiTransactionToRow)
            .filter(Boolean);
          setRowsApi(normalized);
        } else {
          setError(res?.error || "Failed to fetch transactions");
          setRowsApi([]);
        }
      } catch (_) {
        if (active) {
          setError("Network error. Please try again.");
          setRowsApi([]);
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [user?.id, user?.role, search, status]);

  // Function to open confirmation modal
  const handleOpenConfirmModal = (transaction) => {
    setSelectedTransaction(transaction);
    setModalError(null);
    setModalOpen(true);
  };

  // Function to confirm buyer
  const handleConfirmBuyer = async () => {
    if (!selectedTransaction?.id) return;

    setConfirmingId(selectedTransaction.id);
    try {
      const response = await apiService.post(
        `/transactions/${selectedTransaction.id}/confirm-seller`
      );
      if (response?.success) {
        // Refresh the transactions list
        const idParamKey = user?.role === "buyer" ? "buyer_id" : "seller_id";
        const params = {
          [idParamKey]: user.id,
          search: search?.trim() || null,
          status: status !== "all" ? status : null,
          page: 1,
          limit: 100,
        };
        const res = await apiService.get("/transactions/by-user/list", params);
        if (res?.success) {
          const list = res?.data?.transactions;
          const normalized = (Array.isArray(list) ? list : [])
            .map(mapApiTransactionToRow)
            .filter(Boolean);
          setRowsApi(normalized);
        }
        setModalOpen(false);
        setSelectedTransaction(null);
      } else {
        setModalError(response?.error || "Failed to confirm buyer");
      }
    } catch (err) {
      setModalError("Network error. Please try again.");
    } finally {
      setConfirmingId(null);
    }
  };

  // Function to close modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedTransaction(null);
    setModalError(null);
  };

  const rows = useMemo(() => {
    // Client-side safeguard filter on top of server filters
    let r = [...rowsApi];
    if (status !== "all") r = r.filter((x) => x.status === status);
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter(
        (x) =>
          String(x.id).toLowerCase().includes(q) ||
          String(x.listing.title).toLowerCase().includes(q) ||
          String(x.buyer.name).toLowerCase().includes(q)
      );
    }
    return r;
  }, [rowsApi, search, status]);

  return (
    <LayoutBuyerSeller>
      {/* Header */}
      <div className="absolute lg:hidden top-0 left-0 h-72 w-full bg-blue-gradient"></div>
      <div className="relative mb-0 md:mb-8 z-[10]">
        <div className="flex px-5 items-center justify-between">
          <div>
            <h2 className="text-page-title md:text-page-title-d text-white lg:text-[#1E1E1E] font-rubik">
              Transactions
            </h2>
            <p className="text-small-price opacity-50 mt-4 md:text-small-price text-white lg:text-[#1E1E1E] !font-normal">
              Find all of your buyers requests below.
            </p>
          </div>
        </div>

        {/* Toolbar */}
        <div className="mt-8">
          <div className="w-full bg-[#F5F8FF] md:bg-white border-none md:border border-[#DCEDFF] rounded-tl-[25px] lg:rounded-tl-[12px] rounded-tr-[25px] lg:rounded-tr-[12px] rounded-br-[0px] lg:rounded-br-[12px] rounded-bl-[0px] lg:rounded-bl-[12px] p-5 pt-[40px] md:pt-5 grid md:flex items-center gap-4">
            <div className="relative flex-1">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full h-11 rounded-[8px] border border-gray-200 pl-9 pr-[12px] text-caption bg-white"
                placeholder="Search listings..."
              />
              <HiSearch className="absolute left-3 top-3.5 text-gray-400 h-4 w-4" />
            </div>
            <div className="inline-flex items-center gap-2 px-2.5 py-2.5 rounded-[8px] border border-gray-200 bg-white text-caption text-esdarkGray">
              <span aria-hidden>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="25"
                  height="24"
                  viewBox="0 0 25 24"
                  fill="none"
                >
                  <g opacity="0.5">
                    <path
                      d="M3.88589 5.66687C3.12955 4.82155 2.75138 4.39889 2.73712 4.03968C2.72473 3.72764 2.85882 3.42772 3.09963 3.22889C3.37684 3 3.94399 3 5.07828 3H19.9212C21.0555 3 21.6227 3 21.8999 3.22889C22.1407 3.42772 22.2748 3.72764 22.2624 4.03968C22.2481 4.39889 21.8699 4.82155 21.1136 5.66687L15.4074 12.0444C15.2566 12.2129 15.1812 12.2972 15.1275 12.3931C15.0798 12.4781 15.0448 12.5697 15.0236 12.6648C14.9997 12.7721 14.9997 12.8852 14.9997 13.1113V18.4584C14.9997 18.6539 14.9997 18.7517 14.9682 18.8363C14.9403 18.911 14.895 18.9779 14.836 19.0315C14.7692 19.0922 14.6784 19.1285 14.4969 19.2012L11.0969 20.5612C10.7293 20.7082 10.5455 20.7817 10.398 20.751C10.269 20.7242 10.1558 20.6476 10.083 20.5377C9.99975 20.4122 9.99975 20.2142 9.99975 19.8184V13.1113C9.99975 12.8852 9.99975 12.7721 9.97587 12.6648C9.95469 12.5697 9.91971 12.4781 9.87204 12.3931C9.81828 12.2972 9.7429 12.2129 9.59213 12.0444L3.88589 5.66687Z"
                      stroke="#1E1E1E"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                </svg>
              </span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="bg-transparent w-full focus:outline-none text-sm"
              >
                <option value="all">All status</option>
                <option value="pending">Pending</option>
                <option value="accepted">Seller Accepted</option>
                <option value="paid">Payment Completed</option>
                <option value="buyer_confirmed">Buyer Confirmed</option>
                <option value="confirmed">Both Confirmed</option>
                <option value="released">Funds Released</option>
                <option value="disputed">In Dispute</option>
                <option value="resolved">Dispute Resolved</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="hidden md:inline-flex items-center gap-2 px-2.5 py-2.5 rounded-[8px] border border-gray-200 bg-white text-caption text-esdarkGray">
              <span aria-hidden>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="21"
                  height="18"
                  viewBox="0 0 21 18"
                  fill="none"
                >
                  <path
                    d="M15.5 1V17M15.5 17L11.5 13M15.5 17L19.5 13M5.5 17V1M5.5 1L1.5 5M5.5 1L9.5 5"
                    stroke="#1E1E1E"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="bg-transparent w-full focus:outline-none text-sm"
              >
                <option value="recent">Most recent</option>
                <option value="oldest">Oldest</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-4 px-5 bg-[#F5F8FF] md:bg-white">
        {loading && <div className="text-sm text-gray-500 py-4">Loading…</div>}
        {!!error && <div className="text-sm text-red-600 py-4">{error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div className="text-sm text-center text-gray-500 py-4">
            No transactions found.
          </div>
        )}
        {rows.map((row) => (
          <div
            key={row.id}
            className="relative bg-white border border-[#DCEDFF] rounded-[12px] p-4 shadow-sm"
          >
            {/* status pill */}
            {(() => {
              const statusDisplay = getStatusDisplay(
                row.status,
                row.adminVerificationStatus,
                row.paymentCancelledReason
              );
              return (
                <span
                  className={`absolute top-3 right-3 text-[12px] font-semibold px-2.5 py-1 rounded-full capitalize ${
                    STATUS_PILL[statusDisplay.pillKey] ||
                    STATUS_PILL[row.status] ||
                    "bg-gray-100 text-gray-700"
                  }`}
                >
                  {statusDisplay.text}
                </span>
              );
            })()}

            {/* title */}
            <div className="text-[14px] font-semibold text-[#1E1E1E] pr-24">
              {truncateChars(row.listing.title, 16)}
            </div>
            <div className="text-[12px] text-[#1E1E1E] opacity-50">
              {row.listing.type}
            </div>
            <div className="text-[12px] text-[#1E1E1E] opacity-50 mt-1">
              {row.id}
            </div>

            {/* buyer */}
            <div className="flex items-center gap-2 mt-3">
              {row.buyer.avatar ? (
                <img
                  src={row.buyer.avatar}
                  alt="avatar"
                  className="h-6 w-6 rounded-full object-cover"
                />
              ) : (
                <span className="text-slate-600 rounded-full object-cover bg-slate-200 w-6 h-6 text-xl font-semibold flex items-center justify-center">
                  {row.buyer.name?.charAt(0)}
                </span>
              )}
              <div>
                <div className="text-[12px] text-[#1E1E1E] opacity-75">
                  {row.buyer.name}
                </div>
                <div className="text-[10px] text-[#1E1E1E] opacity-50">
                  {row.buyer.email}
                </div>
              </div>
            </div>

            {/* amount */}
            <div className="mt-3 text-[18px] font-bold text-[#000025]">
              {row.amount}
            </div>

            {/* actions */}
            <div className="mt-3 flex items-center gap-3">
              {!row.sellerConfirmed &&
                row.status !== "cancelled" &&
                (row.status === "accepted" ||
                  row.status === "confirmed" ||
                  row.status === "buyer_confirmed") && (
                  <button
                    onClick={() => handleOpenConfirmModal(row)}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#B9FFCC] text-[#16653F] text-[14px] py-2.5 px-3 rounded-[8px]"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="13"
                      viewBox="0 0 18 13"
                      fill="none"
                    >
                      <path
                        d="M17 1L6 12L1 7"
                        stroke="#16653F"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Confirm
                  </button>
                )}
              <button
                className="h-10 w-10 flex items-center justify-center rounded-[8px] border border-gray-200 bg-white"
                title="More"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M2 17V16.8498C2 16.5333 2 16.3751 2.02421 16.2209C2.0457 16.084 2.08136 15.9497 2.13061 15.8202C2.18609 15.6743 2.2646 15.5369 2.42162 15.2622L6 9M2 17C2 19.2091 3.79086 21 6 21C8.20914 21 10 19.2091 10 17M2 17V16.8C2 16.52 2 16.38 2.0545 16.273C2.10243 16.1789 2.17892 16.1024 2.273 16.0545C2.37996 16 2.51997 16 2.8 16H9.2C9.48003 16 9.62004 16 9.727 16.0545C9.82108 16.1024 9.89757 16.1789 9.9455 16.273C10 16.38 10 16.52 10 16.8V17M6 9L9.57838 15.2622C9.7354 15.5369 9.81391 15.6743 9.86939 15.8202C9.91864 15.9497 9.9543 16.084 9.97579 16.2209C10 16.3751 10 16.5333 10 16.8498V17M6 9L18 7M14 15V14.8498C14 14.5333 14 14.3751 14.0242 14.2209C14.0457 14.084 14.0814 13.9497 14.1306 13.8202C14.1861 13.6743 14.2646 13.5369 14.4216 13.2622L18 7M14 15C14 17.2091 15.7909 19 18 19C20.2091 19 22 17.2091 22 15M14 15V14.8C14 14.52 14 14.38 14.0545 14.273C14.1024 14.1789 14.1789 14.1024 14.273 14.0545C14.38 14 14.52 14 14.8 14H21.2C21.48 14 21.62 14 21.727 14.0545C21.8211 14.1024 21.8976 14.1789 21.9455 14.273C22 14.38 22 14.52 22 14.8V15M18 7L21.5784 13.2622C21.7354 13.5369 21.8139 13.6743 21.8694 13.8202C21.9186 13.9497 21.9543 14.084 21.9758 14.2209C22 14.3751 22 14.5333 22 14.8498V15M12 3V8"
                    stroke="#1E1E1E"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Table (desktop) */}
      <div className="hidden md:block w-full overflow-x-auto bg-white border border-[#DCEDFF] rounded-[12px]">
        <table className="w-[97%] ms-5 mr-5 mx-auto mt-5 mb-2 min-w-[900px]">
          <thead className="bg-[#1E1E1E0D] text-subsection-title-d text-[#1E1E1E]">
            <tr>
              <th className="text-left min-w-[150px] py-3 px-5 rounded-tl-[12px]">
                Transaction ID
              </th>
              <th className="text-left min-w-[250px] py-3 px-5">Listing</th>
              <th className="text-left min-w-[200px] py-3 px-5">Buyer</th>
              <th className="text-left min-w-[150px] py-3 px-5">Amount</th>
              <th className="text-left min-w-[220px] py-3 px-5">Status</th>
              <th className="text-left py-3 px-5 rounded-tr-[12px]">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td
                  className="py-6 text-center px-5 text-sm text-gray-500"
                  colSpan={6}
                >
                  Loading…
                </td>
              </tr>
            )}
            {!!error && !loading && (
              <tr>
                <td className="py-6 px-5 text-sm text-red-600" colSpan={6}>
                  {error}
                </td>
              </tr>
            )}
            {!loading && !error && rows.length === 0 && (
              <tr>
                <td
                  className="py-6 text-center px-5 text-sm text-gray-500"
                  colSpan={6}
                >
                  No transactions found.
                </td>
              </tr>
            )}
            {rows.map((row, idx) => (
              <tr key={row.id} className="border-t border-gray-100">
                <td className="py-3 px-5 text-[#000025] opacity-50 text-body ">
                  {idx + 1}
                  {/* {row.id ? row.id : "N/A"} */}
                </td>
                <td className="py-3 px-5">
                  <div className="flex items-center gap-4">
                    <div className="h-[50px] w-[50px] bg-gray-100 rounded overflow-hidden">
                      <img
                        src={row.listing.thumb}
                        alt="thumb"
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div>
                      <div className="font-medium text-[#1E1E1E] text-body line-clamp-1">
                        {row.listing.title}
                      </div>
                      <div className="mt-1 text-[#000025] text-nav-d">
                        {row.listing.type}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="py-3 px-5">
                  <div className="flex items-center gap-4">
                    {row.buyer.avatar ? (
                      <img
                        src={row.buyer.avatar}
                        alt="avatar"
                        className="h-[32px] w-[32px] rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-slate-600 rounded-full object-cover bg-slate-200 w-8 h-8 text-xl font-semibold flex items-center justify-center">
                        {row.buyer.name?.charAt(0)}
                      </span>
                    )}
                    <div>
                      <div className="font-medium text-nav-d text-[#1E1E1E] opacity-75">
                        {row.buyer.name}
                      </div>
                      <div className="text-[12px] opacity-50 text-[#1E1E1E]">
                        {row.buyer.email}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="py-3 px-5 text-[#000025] text-small-price font-bold">
                  {row.amount}
                </td>
                <td className="py-3 px-5">
                  {(() => {
                    const statusDisplay = getStatusDisplay(
                      row.status,
                      row.adminVerificationStatus
                    );
                    return (
                      <span
                        className={`text-[14px] font-semibold px-2.5 py-1 rounded-full capitalize whitespace-nowrap leading-none ${
                          STATUS_PILL[statusDisplay.pillKey] ||
                          STATUS_PILL[row.status] ||
                          "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {statusDisplay.text}
                      </span>
                    );
                  })()}
                </td>
                <td className="py-3 px-5">
                  <div className="flex items-center gap-2">
                    {!row.sellerConfirmed &&
                      (row.status === "accepted" ||
                        row.status === "confirmed" ||
                        row.status === "buyer_confirmed") && (
                        <button
                          onClick={() => handleOpenConfirmModal(row)}
                          className="inline-flex items-center gap-4 bg-[#B9FFCC] text-[#16653F] text-subsection-title py-3 px-3 rounded-[8px]"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="13"
                            viewBox="0 0 18 13"
                            fill="none"
                          >
                            <path
                              d="M17 1L6 12L1 7"
                              stroke="#16653F"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>{" "}
                          Confirm
                        </button>
                      )}
                    {/* <button
                      className="inline-flex items-center gap-4 border border-gray-200 text-esblue text-subsection-title py-2.5 px-3 rounded-[8px]"
                      title="More"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M2 17V16.8498C2 16.5333 2 16.3751 2.02421 16.2209C2.0457 16.084 2.08136 15.9497 2.13061 15.8202C2.18609 15.6743 2.2646 15.5369 2.42162 15.2622L6 9M2 17C2 19.2091 3.79086 21 6 21C8.20914 21 10 19.2091 10 17M2 17V16.8C2 16.52 2 16.38 2.0545 16.273C2.10243 16.1789 2.17892 16.1024 2.273 16.0545C2.37996 16 2.51997 16 2.8 16H9.2C9.48003 16 9.62004 16 9.727 16.0545C9.82108 16.1024 9.89757 16.1789 9.9455 16.273C10 16.38 10 16.52 10 16.8V17M6 9L9.57838 15.2622C9.7354 15.5369 9.81391 15.6743 9.86939 15.8202C9.91864 15.9497 9.9543 16.084 9.97579 16.2209C10 16.3751 10 16.5333 10 16.8498V17M6 9L18 7M14 15V14.8498C14 14.5333 14 14.3751 14.0242 14.2209C14.0457 14.084 14.0814 13.9497 14.1306 13.8202C14.1861 13.6743 14.2646 13.5369 14.4216 13.2622L18 7M14 15C14 17.2091 15.7909 19 18 19C20.2091 19 22 17.2091 22 15M14 15V14.8C14 14.52 14 14.38 14.0545 14.273C14.1024 14.1789 14.1789 14.1024 14.273 14.0545C14.38 14 14.52 14 14.8 14H21.2C21.48 14 21.62 14 21.727 14.0545C21.8211 14.1024 21.8976 14.1789 21.9455 14.273C22 14.38 22 14.52 22 14.8V15M18 7L21.5784 13.2622C21.7354 13.5369 21.8139 13.6743 21.8694 13.8202C21.9186 13.9497 21.9543 14.084 21.9758 14.2209C22 14.3751 22 14.5333 22 14.8498V15M12 3V8"
                          stroke="#1E1E1E"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button> */}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Confirmation Modal */}
      <ConfirmTransactionModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmBuyer}
        transactionId={selectedTransaction?.id}
        itemName={selectedTransaction?.listing?.title}
        isLoading={confirmingId === selectedTransaction?.id}
        errorText={modalError}
      />
    </LayoutBuyerSeller>
  );
};

export default SellerTransactions;
